import { MapPin, Star, ArrowRight } from "lucide-react";
import { SERVICE_AREA_TIERS, SERVICE_AREAS_TOP20 } from "@/lib/data";
import SectionHeading from "@/components/site/SectionHeading";
import { Link } from "react-router-dom";

const TIER_STYLES = {
  "Tier 1": { dot: "bg-[#081537]", label: "text-[#081537]", chip: "border-[#081537]/30 hover:bg-[#081537] hover:text-white" },
  "Tier 2": { dot: "bg-[#081537]/80", label: "text-[#081537]", chip: "border-slate-300 hover:border-[#081537]" },
  "Tier 3": { dot: "bg-[#081537]/60", label: "text-[#081537]", chip: "border-slate-300 hover:border-[#081537]" },
  "Tier 4": { dot: "bg-slate-500", label: "text-slate-700", chip: "border-slate-200 hover:border-slate-400" },
  "Tier 5": { dot: "bg-slate-400", label: "text-slate-700", chip: "border-slate-200 hover:border-slate-400" },
  "Tier 6": { dot: "bg-slate-400", label: "text-slate-600", chip: "border-slate-200 hover:border-slate-400" },
  "Tier 7": { dot: "bg-slate-400", label: "text-slate-600", chip: "border-slate-200 hover:border-slate-400" },
};

export default function ServiceAreasPage() {
  return (
    <div>
      <section className="py-16 lg:py-24 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-sm font-semibold tracking-[0.18em] uppercase text-[#081537] mb-3">Service Areas</div>
          <h1 className="font-[Outfit] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#081537] text-balance max-w-3xl">
            80+ localities across Ernakulam & Kochi.
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl">
            From city centre to the outermost panchayats, our crew knows the roads, the societies, and the lifts. Local expertise everywhere we serve.
          </p>
          <div className="mt-6 flex flex-wrap gap-4 text-sm text-slate-600">
            <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#081537]" /> 7 service tiers</span>
            <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#081537]" /> 80+ areas</span>
            <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#081537]" /> All of Kerala on request</span>
          </div>
        </div>
      </section>

      {/* TOP 20 SPOTLIGHT */}
      <section className="py-16 lg:py-20 bg-[#081537]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-3">
            <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
            <div className="text-xs font-semibold tracking-[0.18em] uppercase text-yellow-400">Top 20 Priority Areas</div>
          </div>
          <h2 className="font-[Outfit] text-3xl sm:text-4xl font-bold text-white max-w-3xl">Where we serve the most customers.</h2>
          <p className="mt-3 text-slate-300 max-w-2xl">These are our highest-demand neighbourhoods with same-day or next-day availability.</p>
          <div className="mt-10 flex flex-wrap gap-3">
            {SERVICE_AREAS_TOP20.map((a, i) => (
              <div
                key={a}
                data-testid={`top20-area-${a.toLowerCase().replace(/\s+/g, "-")}`}
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/15 hover:bg-white hover:text-[#081537] text-white rounded-full px-4 py-2.5 text-sm font-medium transition-all"
              >
                <span className="text-xs font-bold text-yellow-400 group-hover:text-[#081537]">{String(i + 1).padStart(2, "0")}</span>
                <MapPin className="w-3.5 h-3.5" />
                {a}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TIERS */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-14">
          {SERVICE_AREA_TIERS.map((t) => {
            const s = TIER_STYLES[t.tier];
            return (
              <div key={t.tier} data-testid={`tier-${t.tier.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="flex items-baseline gap-4 mb-5">
                  <div className={`inline-flex items-center gap-2 ${s.label}`}>
                    <span className={`w-2.5 h-2.5 rounded-full ${s.dot}`} />
                    <span className="font-[Outfit] text-xl sm:text-2xl font-bold">{t.tier}</span>
                  </div>
                  <span className="text-slate-300">·</span>
                  <span className="font-[Outfit] text-xl sm:text-2xl font-semibold text-slate-700">{t.label}</span>
                  <span className="ml-auto text-xs text-slate-400 font-medium">{t.areas.length} areas</span>
                </div>
                <div className="flex flex-wrap gap-2.5">
                  {t.areas.map((a) => (
                    <span
                      key={a}
                      data-testid={`area-${a.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`inline-flex items-center gap-2 bg-white border rounded-full px-4 py-2 text-sm font-medium text-slate-700 transition cursor-default ${s.chip}`}
                    >
                      <MapPin className="w-3.5 h-3.5 opacity-60" />
                      {a}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="py-16 bg-slate-50 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionHeading overline="Don't see your area?" title="We probably cover it too." subtitle="Our crew travels across Kerala and pan-India. Reach out for any location." align="center" />
          <Link to="/contact" data-testid="areas-contact-btn" className="mt-8 inline-flex bg-[#081537] hover:bg-[#0F2152] text-white rounded-full px-7 py-3.5 font-semibold transition items-center gap-2">
            Contact Us <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
